import { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import Lightbox from "@/pages/about/components/Lightbox";
import useSeo from "@/hooks/useSeo";
import { filmInfo, filmCrew, stills, posterImage, characters } from "@/mocks/movie";

export default function About() {
  useSeo({
    title: "激情丛林电影影片资料 - 导演演员与制作团队",
    description:
      "《激情丛林》影片资料：导演陆云舟、编剧及主演阵容、上映时间与影片类型等基本信息，制作团队介绍、影片海报与剧照展示。",
    keywords: "激情丛林影片资料,导演,演员表,制作团队,上映时间,影片类型",
  });

  const [preview, setPreview] = useState<{ src: string; alt: string } | null>(null);

  const gallery = [
    { src: posterImage, alt: "《激情丛林》主视觉海报", title: "主视觉海报" },
    ...stills.map((s) => ({ src: s.src, alt: s.alt, title: s.title })),
  ];

  return (
    <div className="w-full">
      <PageHero
        title="影片资料"
        subtitle="关于《激情丛林》的全部基础信息：创作团队、影片定位，以及那些构成这部电影质感的画面。"
        crumbs={[{ label: "影片资料" }]}
      />

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[300px_1fr] lg:gap-14">
          <div className="mx-auto w-full max-w-[300px] lg:mx-0">
            <button
              type="button"
              onClick={() => setPreview({ src: posterImage, alt: "《激情丛林》主视觉海报" })}
              className="group relative block w-full overflow-hidden rounded-lg border border-background-800"
              aria-label="查看海报大图"
            >
              <div className="h-[430px] w-full">
                <img
                  src={posterImage}
                  alt="《激情丛林》影片海报"
                  title="激情丛林 电影海报"
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-md bg-background-950/70 text-foreground-100 backdrop-blur">
                <i className="ri-zoom-in-line text-lg"></i>
              </span>
            </button>
          </div>

          <div>
            <SectionHeading eyebrow="Film Info" title="基本信息" />
            <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
              {filmInfo.map((item) => (
                <div key={item.label} className="border-b border-background-800 pb-4">
                  <dt className="font-label text-xs uppercase tracking-widest text-foreground-500">
                    {item.label}
                  </dt>
                  <dd className="mt-1.5 text-sm font-medium text-foreground-100">{item.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/plot"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
              >
                <i className="ri-book-open-line"></i>
                查看剧情介绍
              </Link>
              <Link
                to="/reviews"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-5 py-2.5 font-label text-sm text-foreground-100 transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                <i className="ri-chat-quote-line"></i>
                浏览影评
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-800 bg-background-900/40">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
          <SectionHeading
            eyebrow="Story Background"
            title="背景介绍"
            desc="一次远征背后的创作缘起与主题表达。"
          />
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-5 text-sm leading-8 text-foreground-300">
              <p>
                《激情丛林》的剧本经历了长达四年的打磨。创作团队多次深入雨林进行实地考察，
                记录真实的植被、气候与光线，只为让“丛林”在银幕上不再是一个符号，而成为一个有意志的存在。
              </p>
              <p>
                影片在类型上刻意舍弃了爽快的宣泄，转而采用接近纪录片的克制笔触。
                导演陆云舟表示，他希望观众不是“消费”一段冒险，而是真正和林深一起走完那段旅程，
                直到最后才明白人与自然之间那条看不见的边界。
              </p>
              <p>
                正因如此，影片的每一处细节都服务于同一个主题：我们向自然索取时，
                是否还保留着最起码的敬畏。
              </p>
            </div>
            <div className="panel rounded-lg p-6">
              <h3 className="font-heading text-base font-bold text-foreground-50">主题关键词</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {["敬畏自然", "贪婪与救赎", "空间叙事", "自然光美学", "沉浸声音", "反类型"].map(
                  (t) => (
                    <span
                      key={t}
                      className="rounded-full bg-accent-500/10 px-3 py-1 font-label text-xs text-accent-400"
                    >
                      {t}
                    </span>
                  )
                )}
              </div>
              <p className="mt-6 border-t border-background-800 pt-4 text-xs leading-relaxed text-foreground-500">
                以上内容为演示文本，用于展示影片资料页的信息结构。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        <SectionHeading
          eyebrow="Cast"
          title="主要演员"
          desc="五位核心角色，构成整段旅程的张力。"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {characters.map((c) => (
            <div key={c.id} className="panel overflow-hidden rounded-lg">
              <div className="h-40 w-full overflow-hidden sm:h-44">
                <img
                  src={c.avatar}
                  alt={`${c.name}（${c.actor} 饰）`}
                  title={`${c.name} - 激情丛林电影角色`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="p-3.5">
                <p className="font-heading text-sm font-bold text-foreground-50">{c.actor}</p>
                <p className="mt-0.5 font-label text-xs text-foreground-500">饰 {c.name}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        <SectionHeading eyebrow="Crew" title="制作团队" desc="幕后的每一位创作者。" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {filmCrew.map((m) => (
            <div key={m.role} className="panel rounded-lg p-5">
              <span className="font-label text-xs uppercase tracking-widest text-accent-500">
                {m.role}
              </span>
              <p className="mt-2 font-heading text-base font-bold text-foreground-50">{m.name}</p>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground-400">{m.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto w-full px-4 pb-16 md:px-6">
        <SectionHeading
          eyebrow="Gallery"
          title="海报与剧照"
          desc="点击任意图片，可放大查看细节。"
        />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {gallery.map((g) => (
            <button
              key={g.title}
              type="button"
              onClick={() => setPreview({ src: g.src, alt: g.alt })}
              className="group relative overflow-hidden rounded-lg border border-background-800"
              aria-label={`查看 ${g.title} 大图`}
            >
              <div className="h-36 w-full sm:h-40">
                <img
                  src={g.src}
                  alt={g.alt}
                  title={`${g.title} - 激情丛林电影`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <span className="absolute inset-0 flex items-center justify-center bg-background-950/0 opacity-0 transition-all duration-300 group-hover:bg-background-950/50 group-hover:opacity-100">
                <i className="ri-zoom-in-line text-2xl text-foreground-50"></i>
              </span>
            </button>
          ))}
        </div>
      </section>

      {preview && <Lightbox src={preview.src} alt={preview.alt} onClose={() => setPreview(null)} />}
    </div>
  );
}